export interface UserSettings {
  _id?: string;
  mouse: MouseSettings;
  graphics: GraphicsSettings;
  theme: ThemeSettings;
  accessibility?: AccessibilitySettings;
  defaultUnitSystem?: 'metric' | 'imperial';
  betaFeatures?: string[];
  softJawsPresets?: SoftJawsPreset[];
  /** A custom preset id or a built-in `builtin:` id; null means no default. */
  defaultSoftJawsPresetId?: string | null;
  /** Where a project opens from the cloud cabinet; absent means 'ask'. */
  projectOpenMode?: ProjectOpenMode;
  /** Projects kept open in the cloud cabinet, in the order they were opened. */
  openProjects?: OpenProjectRecord[];
  /** How the cabinet projects list is sorted and paged; null until the user changes it. */
  projectListView?: ProjectListView | null;
}

export interface ProjectListView {
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  pageSize: number;
}

/** A user-saved SoftJaws base jaw size. Dimensions are in mm. */
export interface SoftJawsPreset {
  id: string;
  name: string;
  length: number;
  width: number;
  height: number;
  cutterDiameter: number;
}

export interface MouseSettings {
  left: string;
  middle: string;
  right: string;
  doubleMiddle: string;
  /** Swaps which wheel direction zooms in; absent means off. */
  invertWheelZoom?: boolean;
}

/**
 * Mirrors the editor engine's `LightingStyle`
 * (`libs/editor-features/src/engine/graphics-options.ts`) by literal value,
 * not by import — platform-core sits below editor-features and must not
 * depend on it.
 */
export type LightingStyle = 'studio' | 'soft' | 'flat';

export interface GraphicsSettings {
  quality: 'low' | 'medium' | 'high';
  antialiasing: boolean;
  /** Absent on documents saved before this setting existed. */
  lighting?: LightingStyle;
  /**
   * "Enhanced UI": pointer glow, button press feedback, glass viewport
   * overlays and focus-ring fade. Absent means on -- read it through
   * `isEnhancedUiEnabled()`.
   */
  enhancedUi?: boolean;
}

/** Enhanced UI is on unless the user has turned it off. */
export const DEFAULT_ENHANCED_UI = true;

/** Whether Enhanced UI is on for a settings document; only an explicit `false` turns it off. */
export function isEnhancedUiEnabled(
  settings: { graphics?: { enhancedUi?: unknown } | null } | null | undefined
): boolean {
  const value = settings?.graphics?.enhancedUi;
  return typeof value === 'boolean' ? value : DEFAULT_ENHANCED_UI;
}

/** Graphics defaults: High quality (`UPSCALING` 0) and Studio lighting. */
export const DEFAULT_GRAPHICS = { UPSCALING: 0, lighting: 'studio' as LightingStyle };

/** A stored graphics object with any missing quality or lighting filled from the defaults. */
export function withDefaultGraphics<T extends object>(graphics: T | null | undefined): T & typeof DEFAULT_GRAPHICS {
  return { ...DEFAULT_GRAPHICS, ...(graphics ?? {}) } as T & typeof DEFAULT_GRAPHICS;
}

export interface ThemeSettings {
  theme: 'light' | 'dark' | 'system';
}

export interface AccessibilitySettings {
  /** Documents saved before the palettes existed hold a boolean (true = cividis). */
  colorBlindMode: 'off' | 'cividis' | 'viridis' | boolean;
}

/** Where a project opens from the cloud cabinet. */
export type ProjectOpenMode = 'ask' | 'here' | 'new-tab';

/** Which editor an open project uses. */
export type OpenProjectKind = 'editor' | 'mold' | 'jaws';

/** A project kept open in the cloud cabinet. Dates are ISO 8601 strings. */
export interface OpenProjectRecord {
  id: string;
  name: string;
  kind: OpenProjectKind;
  openedAt: string;
  lastActiveAt: string;
}

/** The most projects the cloud cabinet keeps open at once. */
export const MAX_OPEN_PROJECTS = 10;

/** The longest project name stored with an open project. */
export const MAX_OPEN_PROJECT_NAME_LENGTH = 200;

export const PROJECT_OPEN_MODES: readonly ProjectOpenMode[] = ['ask', 'here', 'new-tab'];

/** A stored open mode, or 'ask' for anything missing or unknown. */
export function normalizeOpenMode(value: unknown): ProjectOpenMode {
  return PROJECT_OPEN_MODES.find((mode) => mode === value) ?? 'ask';
}

/** The editor a project type opens in. */
export function openProjectKindOf(projectType: string | null | undefined): OpenProjectKind {
  if (projectType === 'soft_jaws') return 'jaws';
  if (projectType === 'mold') return 'mold';
  return 'editor';
}
